import { ref, readonly } from "vue";
import { defineStore } from "pinia";
import { Currency } from "fungi-types";
import { makeApi } from "~/api";
import { BigNumber } from "ethers";
import useConvert from "~/composables/useConvert";
import { sub } from "date-fns";
import { NewPolRates, PolRates, Scarcity, SocketEventType } from "fungi-types";
import socketService from "~/services/socketService";

const api = makeApi(`${import.meta.env.VITE_API_URL}/pol-price`);

const usePOLPrice = defineStore("POLPrice", () => {
  const maxRateTimeoutS = 70;
  const maxRateTimeoutMs = maxRateTimeoutS * 1_000;

  const { weiToMatic, maticToWei } = useConvert;
  const lastUpdateTime = ref<Date>(sub(new Date(), { seconds: maxRateTimeoutS }));
  const rates = ref<PolRates>({
    updatedAt: "",
    [Currency.EUR]: 0,
    [Currency.USD]: 0,
  });

  function getFloorPrice(scarcity: Scarcity, currentWei: BigNumber): BigNumber {
    if (currentWei.isZero()) return currentWei;
    let minEuroAmount = 2;
    if (scarcity === Scarcity.PLATINUM) minEuroAmount = 75;
    else if (scarcity === Scarcity.GOLD) minEuroAmount = 15;

    const wpol = currencyToPol(minEuroAmount, Currency.EUR);
    const wei = maticToWei(wpol);
    if (currentWei.gt(wei)) {
      return currentWei;
    }
    return wei;
  }

  const refreshInterval = ref<NodeJS.Timeout | null>(null);
  const isInitialized = ref(false);

  async function refreshRates() {
    try {
      const { data } = await api.get("/");

      lastUpdateTime.value = new Date();
      rates.value = data as PolRates;
    } catch (error) {
      console.error("Error fetching POL rates:", error);
    }
  }

  // Initialize function - must be called manually
  async function initialize() {
    if (isInitialized.value) return;

    isInitialized.value = true;

    try {
      // Initial refresh
      await refreshRates();

      console.log("Initializing POL price store...");

      // Set up interval
      refreshInterval.value = setInterval(refreshRates, maxRateTimeoutMs);
      socketService.on(SocketEventType.POL_RATES_UPDATED, async (event) => {
        rates.value = event;
        lastUpdateTime.value = new Date();

        // Reset interval
        if (refreshInterval.value) {
          clearInterval(refreshInterval.value);
        }

        refreshInterval.value = setInterval(refreshRates, maxRateTimeoutMs);
      });
    } catch (error) {
      console.error("Error initializing POL price store:", error);
      isInitialized.value = false; // Reset so it can be retried
    }
  }

  function weiToCurrency(wei: BigNumber, currency: Currency.EUR | Currency.USD): number {
    return weiToMatic(wei) * rates.value[currency];
  }

  function currencyToPol(value: number, currency: Currency.EUR | Currency.USD) {
    if (rates.value[currency as keyof NewPolRates] === 0) return 0;

    return value / rates.value[currency];
  }

  function polToCurrency(pol: number, currency: Currency.EUR | Currency.USD): number {
    return pol * rates.value[currency];
  }

  function currencyToWei(value: number, currency: Currency.EUR | Currency.USD): BigNumber {
    return maticToWei(currencyToPol(value, currency));
  }

  return {
    rates,
    lastUpdateTime,
    refreshRates,
    weiToCurrency,
    currencyToPol,
    polToCurrency,
    currencyToWei,
    getFloorPrice,
    maticToWei,
    initialize,
    isInitialized: readonly(isInitialized), // Make it readonly for external access
  };
});

export default usePOLPrice;
